/**
 * Locale provider + useTranslation hook.
 *
 * The active content locale is driven by the URL: the `/:lang` route segment
 * (none for the default locale) is resolved by the locale layout route and
 * passed to `<LocaleProvider locale={…}>`. CMS hooks read it from this context
 * and scope their cache keys and requests to it. The provider also keeps
 * `<html lang>` in sync on client-side language switches.
 *
 * Usage:
 *   const { t, locale } = useTranslation()
 *   t.nav.skipLink   // typed access, no string keys
 */
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react'
import { messages, DEFAULT_LOCALE, type Locale, type Messages } from './messages'

type LocaleCtx = {
  locale: Locale
  t: Messages
}

const Ctx = createContext<LocaleCtx | null>(null)

export function LocaleProvider({
  locale = DEFAULT_LOCALE,
  children,
}: {
  locale?: Locale
  children: ReactNode
}) {
  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  const value = useMemo<LocaleCtx>(() => ({ locale, t: messages[locale] }), [locale])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

/** Read messages for the active locale. Works without a provider too (returns
 *  the default locale), so a component rendered outside the layout — e.g. by
 *  the root error boundary — never crashes. */
export function useTranslation(): LocaleCtx {
  const ctx = useContext(Ctx)
  if (ctx) return ctx
  return { locale: DEFAULT_LOCALE, t: messages[DEFAULT_LOCALE] }
}
